// w32 Bahor Bog'i — variant that renders the standalone HTML at
// /templates/garden-arch/index.html (lush watercolor garden-arch
// wedding invitation: cream + sage + blush + gold, Cormorant Garamond
// + Pinyon Script; splash screen, falling-petal ambiance, gold-shimmer
// names, scroll-reveal sections, live countdown) inside a full-bleed
// iframe.
//
// The HTML's customizer accepts DD.MM.YYYY directly — names/date/time/
// venue/address pass through raw on the query string.

import type { VariantProps } from "./types";
import { getLocale } from "@/lib/i18n";

// Flat .html — NOT a nested index.html. Go's http.FileServer 301-redirects
// any request ending in /index.html to the bare directory, which then falls
// through to the SPA and renders the wrong template. Every static template
// in this repo is a flat file for exactly this reason.
const TEMPLATE_HTML = "/templates/garden-arch.html";

export function GardenArchStaticVariant({ t, ctx }: VariantProps) {
  const params = new URLSearchParams();
  if (t.names) params.set("names", t.names);
  if (t.date) params.set("date", t.date);
  if (t.time) params.set("time", t.time);
  if (t.venue) params.set("venue", t.venue);
  if (t.address) params.set("address", t.address);
  if (t.mapUrl) params.set("map_url", t.mapUrl);
  // Per-row extras surfaced via the editor — see data.ts w32 manifest.
  // Two independent parent lines: ?parents= drives the top ".inv-family"
  // (groom side); ?parents2= drives the bottom "daughter of …" caption
  // (bride side). Existing invitations only carry parents — the HTML
  // falls the bottom back to it, so they render exactly as before.
  const parents = typeof t.custom?.parents === "string" ? t.custom.parents.trim() : "";
  if (parents) params.set("parents", parents);
  const parents2 = typeof t.custom?.parents2 === "string" ? t.custom.parents2.trim() : "";
  if (parents2) params.set("parents2", parents2);
  const footerNote = typeof t.custom?.footer_note === "string" ? t.custom.footer_note.trim() : "";
  if (footerNote) params.set("footer_note", footerNote);
  // "Bilib qo'ying" info card. Title only forwarded when non-empty so
  // the locale default is preserved otherwise. Body is forwarded even
  // when blank — the customizer treats blank as "hide the section",
  // matching the schedule's opt-out behaviour.
  const infoTitle = typeof t.custom?.info_title === "string" ? t.custom.info_title.trim() : "";
  if (infoTitle) params.set("info_title", infoTitle);
  const infoTextRaw = t.custom?.info_text;
  if (typeof infoTextRaw === "string") params.set("info_text", infoTextRaw);
  // Owner-chosen default language for guests opening the invitation
  // for the first time. The iframe's switcher already prefers ?lang=
  // over localStorage so this becomes the boot language without
  // overriding a guest's explicit later choice on the same device.
  const defaultLang = typeof t.custom?.default_lang === "string" ? t.custom.default_lang : "";
  if (defaultLang && /^(uz|en|ru)$/.test(defaultLang)) params.set("lang", defaultLang);
  // Schedule rides as a JSON-encoded array of {time, title, description}
  // because URL params don't natively carry repeating objects. Always
  // forward when the user has touched the field — including the empty
  // case — so the customizer can distinguish "owner opted out" (hide
  // the timeline section entirely) from "never edited" (render the
  // hardcoded sample rows for gallery preview).
  const schedule = Array.isArray(t.custom?.schedule) ? t.custom.schedule : null;
  if (schedule !== null) params.set("schedule", JSON.stringify(schedule));
  if (ctx.slug) params.set("slug", ctx.slug);
  // ?audio=/music/<id>.mp3 — owner-picked soundtrack from the
  // curated MusicSelector library. Empty/unset = silent card.
  if (t.musicTrackId) params.set("audio", "/music/" + t.musicTrackId + ".mp3");

  // Demo/catalog preview (no real invitation slug): default the template's
  // in-iframe language to the app locale, so taklifla.com previews render in
  // English (and a RU viewer sees Russian). The HTMLs read ?lang= (all) and
  // ?default_lang= (newer premium ones) — set both. Owner-published
  // invitations (ctx.slug set) keep their own default_lang / built-in default.
  if (!ctx.slug) {
    const _loc = getLocale();
    if (!params.has("lang")) params.set("lang", _loc);
    if (!params.has("default_lang")) params.set("default_lang", _loc);
  }
  const src = `${TEMPLATE_HTML}?${params.toString()}`;

  return (
    <iframe
      src={src}
      title={t.names || "Bahor Bog'i"}
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100svh",
        border: "none",
        display: "block",
        background: "#FDF6EE",
      }}
    />
  );
}
